import { deleteStowagePlanItem, getAllStowagePlanItems } from 'src/api/stowage/offlineStowageApi';
import useApi from 'src/api/useApi';
import { useSWService } from 'src/api/sw/useSWService';

const useSyncOfflineStowageData = () => {
    const { postApiData } = useApi();
    const { refreshRequestsContaining } = useSWService();

    const syncStowagePlanData = async (): Promise<void> => {
        try {
            const items = await getAllStowagePlanItems();
            items.sort((a, b) => a.lastUpdated - b.lastUpdated);

            for (const item of items) {
                let allRequestsSuccessful = true;

                try {
                    // 1. Sync addStowagePlanRequest first (creates the plan)
                    if (item.addStowagePlanRequest) {
                        const response = await postApiData({
                            fullUrl: item.addStowagePlanRequest.url,
                            data: item.addStowagePlanRequest.body,
                            shouldReturnError: true,
                            skipRefreshCacheEndpoints: true,
                        });
                        if (!response || response instanceof Error) {
                            console.warn('Sync addStowagePlanRequest failed, will retry next cycle.', item.stowagePlanGuid, response);
                            allRequestsSuccessful = false;
                        }
                    }

                    // 2. Sync updateStowagePlanRequest (updates plan metadata) — only if previous step succeeded
                    if (allRequestsSuccessful && item.updateStowagePlanRequest) {
                        const response = await postApiData({
                            fullUrl: item.updateStowagePlanRequest.url,
                            data: item.updateStowagePlanRequest.body,
                            shouldReturnError: true,
                            skipRefreshCacheEndpoints: true,
                        });
                        if (!response || response instanceof Error) {
                            console.warn('Sync updateStowagePlanRequest failed, will retry next cycle.', item.stowagePlanGuid, response);
                            allRequestsSuccessful = false;
                        }
                    }

                    // 3. Sync saveStowageDataRequest[] (plan changes) — only if previous steps succeeded
                    if (allRequestsSuccessful && item.saveStowageDataRequest) {
                        const requests = Array.isArray(item.saveStowageDataRequest)
                            ? item.saveStowageDataRequest
                            : [item.saveStowageDataRequest];

                        for (const request of requests) {
                            const response = await postApiData({
                                fullUrl: request.url,
                                data: request.body,
                                shouldReturnError: true,
                                skipRefreshCacheEndpoints: true,
                            });
                            if (!response || response instanceof Error) {
                                console.warn('Sync saveStowageDataRequest failed, will retry next cycle.', item.stowagePlanGuid, response);
                                allRequestsSuccessful = false;
                                break;
                            }
                        }
                    }
                } catch (error) {
                    console.warn('An unexpected error occurred while syncing stowage item.', item.stowagePlanGuid, error);
                    allRequestsSuccessful = false;
                }

                if (allRequestsSuccessful) {
                    await deleteStowagePlanItem(item.stowagePlanGuid);
                }
            }

            await refreshRequestsContaining(['GetStowagePlans', 'GetMasterStowagePlan', 'GetStowagePlanChanges']);
        } catch (error) {
            console.warn('Failed to sync offline stowage data.', error);
        }
    };

    return {
        syncStowagePlanData,
    };
};

export default useSyncOfflineStowageData;
